import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Card, CardContent, Grid, Divider,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import QRCode from 'react-qr-code';
import { verifyAPI } from '../services/api';

export default function VerifyCertificate() {
  const { certificateId } = useParams();
  const [searchParams] = useSearchParams();
  const [certId, setCertId] = useState(certificateId || searchParams.get('id') || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (certificateId) handleVerify(certificateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateId]);

  const handleVerify = async (id) => {
    const verifyId = id || certId;
    if (!verifyId.trim()) {
      setError('Please enter a Card / Certificate ID');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await verifyAPI.verify(verifyId.trim());
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setResult({ valid: false, message: 'Card not found' });
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const baseUrl = window.location.origin;
  const card = result?.card;

  const DetailRow = ({ label, value }) => (
    <Grid item xs={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight="bold">{value || '—'}</Typography>
    </Grid>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ maxWidth: 720, width: '100%', boxShadow: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" color="primary" gutterBottom>
            Smart Card Verification
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
            Enter the Card / Certificate ID to verify its authenticity
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              fullWidth variant="outlined" placeholder="Enter Card / Certificate ID (e.g. CERT-...)"
              value={certId} onChange={(e) => setCertId(e.target.value)}
              onKeyUp={(e) => e.key === 'Enter' && handleVerify()}
            />
            <Button variant="contained" onClick={() => handleVerify()} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <SearchIcon />}
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {result && (
            <Paper sx={{ p: 3, bgcolor: result.valid ? '#f1f8e9' : '#fce4ec' }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {result.valid ? (
                  <VerifiedIcon sx={{ fontSize: 60, color: '#2e7d32' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 60, color: '#d32f2f' }} />
                )}
                <Typography variant="h5" fontWeight="bold" color={result.valid ? 'success.main' : 'error.main'}>
                  {result.valid ? 'VALID CARD' : 'INVALID CARD'}
                </Typography>
              </Box>

              {card && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={{ color: '#1a237e', fontSize: 13, fontWeight: 700, mb: 1.5, letterSpacing: 1 }}>
                    Gemological testing laboratory & Research center
                  </Typography>
                  <Grid container spacing={1.5}>
                    <DetailRow label="Card ID" value={card.card_id} />
                    <DetailRow label="Serial Number" value={card.serial_number} />
                    <DetailRow label="Gemstone Name" value={card.gemstone_name} />
                    <DetailRow label="Weight" value={card.weight} />
                    <DetailRow label="Color" value={card.color} />
                    <DetailRow label="Shape & Cut" value={card.shape_and_cut} />
                    <DetailRow label="Species" value={card.species} />
                    <DetailRow label="Cut" value={card.cut} />
                    <DetailRow label="Origin" value={card.origin} />
                    <DetailRow label="Quality" value={card.quality_grade} />
                    <DetailRow label="Metal" value={card.metal} />
                    <DetailRow label="Finish" value={card.finish} />
                    <DetailRow label="Product" value={card.product_title} />
                    <DetailRow label="Issue Place" value={card.issue_place} />
                    {card.comments && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Comments</Typography>
                        <Typography variant="body2">{card.comments}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {!card && result.message && (
                <Typography textAlign="center" color="error" sx={{ mt: 2 }}>
                  {result.message}
                </Typography>
              )}

              {card && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <QRCode value={`${baseUrl}/verify/${card.card_id}`} size={90} />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Scan QR to verify
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
